import {useQuery} from '@apollo/client';
import {Alert, Form, Select} from 'antd';
import {GET_STOCK_PICK_MARKETS} from '../../resolvers';
import {
  exchangesNotEnabled,
  exchangesWithoutPicks,
} from '../../subscriptions/PlanSubscriptionsHelpers';
import CatalogModalSection from './CatalogModalSection';

/**
 * Which exchanges a Daba Pro item unlocks stock picks for.
 *
 * Every Pro item lists its exchanges: one is a single-exchange plan, several
 * is Global. Global covers only what it lists, so a newly enabled exchange is
 * not in it until added here. Only exchanges with stock picks enabled are
 * offered; the backend refuses an item with none.
 */
const CatalogExchangeScopeSection = () => {
  const form = Form.useFormInstance();
  const exchangeCodes = Form.useWatch('exchangeCodes', form) || [];
  const {data, loading} = useQuery(GET_STOCK_PICK_MARKETS, {
    errorPolicy: 'all',
  });
  const markets = Array.isArray(data?.getStockPickMarkets)
    ? data.getStockPickMarkets
    : null;
  const notEnabled = markets ? exchangesNotEnabled(exchangeCodes, markets) : [];
  const withoutPicks = markets
    ? exchangesWithoutPicks(exchangeCodes, markets).filter(
        code => !notEnabled.includes(code),
      )
    : [];

  return (
    <CatalogModalSection
      title="Exchange coverage"
      description="Which markets this Pro plan unlocks stock picks for. Pick several for a Global plan.">
      <Form.Item
        label="Exchanges"
        name="exchangeCodes"
        extra={
          exchangeCodes.length > 1
            ? `Global plan: covers ${exchangeCodes.join(
                ', ',
              )} only. Add new exchanges here as they launch.`
            : null
        }
        rules={[
          {
            required: true,
            type: 'array',
            min: 1,
            message: 'Pick at least one exchange',
          },
        ]}>
        <Select
          mode="multiple"
          loading={loading}
          showSearch
          optionFilterProp="label"
          placeholder="e.g. NGX"
          notFoundContent={
            loading ? null : 'No exchange has stock picks enabled'
          }
          options={(markets || []).map(market => ({
            value: market.code,
            label: market.exchange?.stockExchangeName
              ? `${market.code} · ${market.exchange.stockExchangeName}`
              : market.code,
          }))}
        />
      </Form.Item>

      {notEnabled.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Stock picks are not enabled on ${notEnabled.join(', ')}`}
          description="The plan is not sold for these exchanges. Enable stock picks on the exchange, or remove it here."
        />
      )}

      {withoutPicks.length > 0 && (
        <Alert
          type="info"
          showIcon
          message={`No published stock picks yet for ${withoutPicks.join(
            ', ',
          )}`}
          description="The plan can be created and sold now. Its picks unlock once the exchange's first edition is published."
        />
      )}
    </CatalogModalSection>
  );
};

export default CatalogExchangeScopeSection;
